import { useEffect, useLayoutEffect, useState, type CSSProperties, type RefObject } from "react";

/**
 * Wohin ein Menü unter (oder über) seinem Knopf kommt — rein, getestet.
 * **Nie über den Knopf**: unter ihm, oder darüber, wenn unten kein Platz ist —
 * so liegt unter dem Zeiger nie ein Eintrag (der Grund, warum es das Menü
 * statt eines nativen `<select>` gibt, siehe ui/select-menu.tsx).
 */
export function placeMenu(
  anchor: { left: number; top: number; bottom: number; width: number },
  menu: { width: number; height: number },
  viewport: { width: number; height: number },
  gap = 4,
  margin = 8,
): { left: number; top: number; minWidth: number } {
  const left = Math.max(margin, Math.min(anchor.left, viewport.width - menu.width - margin));
  const top = anchor.bottom + menu.height + margin > viewport.height
    ? Math.max(margin, anchor.top - menu.height - gap)
    : anchor.bottom + gap;
  return { left: Math.round(left), top: Math.round(top), minWidth: Math.round(anchor.width) };
}

/**
 * **Ein Menü, das am `<body>` hängt** (`createPortal`) und `position: fixed`
 * liegt — übernommen aus `client/src/react/hooks/use-popover.ts` (die
 * Plattform teilt die Muster, nicht den Code — docs/11 10):
 *
 * - **Öffnet auf click** (der Aufrufer), nie auf mousedown: Der Klick, der es
 *   aufmacht, ist verbraucht und kann keinen Eintrag treffen.
 * - **Nie über dem Knopf** (`placeMenu`).
 * - **Außenklick über `mousedown` in der Capture-Phase.**
 * - **Schließt beim Scrollen — außer in der eigenen Liste**
 *   (`menu.contains(e.target)`), sonst schlösse der erste Rad-Tick in ihr es.
 * - Escape schließt (Capture-Phase, damit kein anderer Escape-Handler dran ist).
 */
export function usePopover({
  open,
  onClose,
  anchorRef,
  menuRef,
  gap = 4,
  margin = 8,
}: {
  open: boolean;
  onClose: () => void;
  anchorRef: RefObject<HTMLElement | null>;
  menuRef: RefObject<HTMLElement | null>;
  gap?: number;
  margin?: number;
}): CSSProperties {
  const [style, setStyle] = useState<CSSProperties>(HIDDEN);

  useLayoutEffect(() => {
    if (!open) return;
    const anchor = anchorRef.current;
    const menu = menuRef.current;
    if (!anchor || !menu) return;
    const size = menu.getBoundingClientRect();
    const place = placeMenu(anchor.getBoundingClientRect(), size, { width: window.innerWidth, height: window.innerHeight }, gap, margin);
    // Messen geht erst, wenn das Menü im Dokument liegt — daher hier und nicht im Rendern.
    setStyle({ position: "fixed", ...place });
  }, [open, anchorRef, menuRef, gap, margin]);

  useEffect(() => {
    if (!open) return;
    const inside = (target: EventTarget | null): boolean => {
      const node = target instanceof Node ? target : null;
      return Boolean(node && (menuRef.current?.contains(node) || anchorRef.current?.contains(node)));
    };
    const onOutside = (e: MouseEvent) => { if (!inside(e.target)) onClose(); };
    const onScroll = (e: Event) => {
      const node = e.target instanceof Node ? e.target : null;
      if (!node || !menuRef.current?.contains(node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      onClose();
      anchorRef.current?.focus();
    };
    document.addEventListener("mousedown", onOutside, true);
    document.addEventListener("keydown", onKey, true);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onClose);
    return () => {
      document.removeEventListener("mousedown", onOutside, true);
      document.removeEventListener("keydown", onKey, true);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onClose);
    };
  }, [open, onClose, anchorRef, menuRef]);

  return open ? style : HIDDEN;
}

const HIDDEN: CSSProperties = { position: "fixed", visibility: "hidden" };

/**
 * Pfeiltasten in einer Liste von Knöpfen: der nächste benutzbare Eintrag,
 * an den Enden bleibt er stehen. `at = -1` (nichts fokussiert) → der erste.
 */
export function nextIndex(at: number, count: number, key: "ArrowDown" | "ArrowUp"): number {
  if (count <= 0) return -1;
  const next = key === "ArrowDown" ? at + 1 : at <= 0 ? 0 : at - 1;
  return Math.max(0, Math.min(count - 1, next));
}

/** Pfeiltasten in einer Liste von Knöpfen (role="option"); `true`, wenn die Taste verbraucht ist. */
export function moveFocus(list: HTMLElement | null, key: string): boolean {
  if (!list || (key !== "ArrowDown" && key !== "ArrowUp")) return false;
  const usable = Array.from(list.querySelectorAll<HTMLButtonElement>("button:not([disabled])"));
  const at = usable.indexOf(document.activeElement as HTMLButtonElement);
  usable[nextIndex(at, usable.length, key)]?.focus();
  return true;
}
